/**
 * @file BoundingBoxIntersectionTool.tsx
 * @description Tool for analyzing bounding box intersections and spatial relationships between elements
 */

import { useState, useCallback } from "react";
import {
  Search,
  Eye,
  Download,
  ChevronDown,
  ChevronRight,
  Box,
  AlertCircle,
} from "lucide-react";
import { useViewerStore } from "@/store/viewerStore";
import { PropertiesService } from "@/services/core/propertiesTools";
import { toast } from "@/components/common/Toast";
import {
  visualizeBoundingBox,
  clearBoundingBoxOverlay,
} from "./utils/visualizationUtils";

interface ExpansionValues {
  x: number; // feet
  y: number; // feet
  z: number; // feet
}

interface ElementResult {
  dbId: number;
  name: string;
  category: string;
  distance: number; // center-to-center distance in feet
  included: boolean;
  excludeReason?: string;
}

interface IntersectionResult {
  source: {
    dbId: number;
    name: string;
    originalBox: any;
    expandedBox: any;
  };
  elements: ElementResult[];
  statistics: {
    totalFound: number;
    includedCount: number;
    excludedCount: number;
    searchTime: number; // ms
  };
  expansion: ExpansionValues;
}

// Default exclusion lists
const DEFAULT_EXCLUSIONS = {
  // Exact name matches (case-insensitive)
  exactNames: [
    "Model",
    "Level",
    "Grid",
    "Scope Box",
    "Reference Plane",
    "Section Box",
    "Camera",
    "View",
    "Schedule",
    "Sheet",
  ],
  // Partial name matches (contains these strings)
  partialNames: [
    "Annotation",
    "Tag",
    "Dimension",
    "Detail",
    "Symbol",
    "Text",
    "Arrow",
    "Line Style",
    "Legend",
    ".dwg",
    ".rvt",
    ".ifc",
    ".nwc",
    ".nwd", // Linked files
    "Project",
    "MLB", // Project metadata
  ],
  // Category-based exclusions
  categories: [
    "Revit Analytical",
    "Mass",
    "Entourage",
    "Planting",
    "Site",
    "Topography",
    "Views",
    "Sheets",
    "Schedules",
    "Legends",
    "Annotations",
    "Revit Level",
    "Revit Levels", // Level elements
    "RVT Link",
    "CAD Link",
    "IFC Link", // Linked models
  ],
  // Special exclusions for non-physical elements
  specialExclusions: {
    // Check if category matches the name (like "Revit 2022-017 MLB Project Vista")
    categoryMatchesName: true,
    // Check for specific patterns
    patterns: [
      /^Revit\s+\d{4}-\d{3}/, // Pattern like "Revit 2022-017"
      /^Revit\s+.*\.(dwg|rvt|ifc|nwc|nwd)/, // Pattern for linked files
      /^\d+(ST|ND|RD|TH)\s+FLOOR$/i, // Floor levels like "1ST FLOOR"
      /^(ROOF|GRADE|T\.O\.|B\.O\.|LEVEL)/i, // Other level names
    ],
  },
};

export function BoundingBoxIntersectionTool() {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [sourceElementId, setSourceElementId] = useState("");
  const [expansion, setExpansion] = useState<ExpansionValues>({
    x: 5,
    y: 5,
    z: 0,
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState<IntersectionResult | null>(null);
  const [showExcluded, setShowExcluded] = useState(false);
  const [showBoundingBox, setShowBoundingBox] = useState(false);

  const { viewer, isModelLoaded } = useViewerStore();

  // Helper function to get Forge Viewer's internal THREE.js instance
  const getTHREE = useCallback(() => {
    if (!viewer) return null;
    return (window as any).THREE || (viewer as any).impl?.renderer?.THREE;
  }, [viewer]);

  // Expand bounding box by given values
  const expandBoundingBox = useCallback(
    (originalBox: any, expansion: ExpansionValues): any => {
      if (!viewer?.model) return originalBox;

      const THREE = getTHREE();
      if (!THREE) {
        return originalBox;
      }

      // Get unit scale to convert to meters
      const unitScale = viewer.model.getUnitScale();

      // Convert feet to model units
      const FEET_TO_METERS = 0.3048;
      const xExpansion = (expansion.x * FEET_TO_METERS) / unitScale;
      const yExpansion = (expansion.y * FEET_TO_METERS) / unitScale;
      const zExpansion = (expansion.z * FEET_TO_METERS) / unitScale;

      const expandedBox = originalBox.clone();
      expandedBox.min.x -= xExpansion;
      expandedBox.min.y -= yExpansion;
      expandedBox.min.z -= zExpansion;
      expandedBox.max.x += xExpansion;
      expandedBox.max.y += yExpansion;
      expandedBox.max.z += zExpansion;


      return expandedBox;
    },
    [viewer, getTHREE]
  );

  // Find all elements intersecting with the expanded box
  const findIntersectingElements = useCallback(
    (expandedBox: any): number[] => {
      if (!viewer?.model) return [];

      const intersectingDbIds: number[] = [];
      const instanceTree = viewer.model.getInstanceTree();

      if (!instanceTree) {
        return [];
      }

      // Collect all object IDs (including both leaf nodes and objects with fragments)
      const allDbIds: number[] = [];
      const processedIds = new Set<number>();

      // Method 1: Get all dbIds that have fragments (these are the actual visible elements)
      const fragList = viewer.model.getFragmentList();
      const fragments = fragList.fragments;

      if (fragments && fragments.fragId2dbId) {
        // Get unique dbIds from fragment list
        for (let i = 0; i < fragments.fragId2dbId.length; i++) {
          const dbId = fragments.fragId2dbId[i];
          if (dbId && !processedIds.has(dbId)) {
            processedIds.add(dbId);
            allDbIds.push(dbId);
          }
        }
      }

      // Method 2: Also check leaf nodes as fallback
      if (allDbIds.length === 0) {
        instanceTree.enumNodeChildren(
          instanceTree.getRootId(),
          (dbId: number) => {
            // Check if this is a leaf node
            if (instanceTree.getChildCount(dbId) === 0) {
              if (!processedIds.has(dbId)) {
                processedIds.add(dbId);
                allDbIds.push(dbId);
              }
            }
          },
          true // recursive
        );
      }

      // Check each element for intersection
      let checkedCount = 0;
      let boxFailures = 0;

      const THREE = getTHREE();
      if (!THREE) {
        return [];
      }

      allDbIds.forEach((dbId) => {
        const elementBox = new THREE.Box3();
        let hasBox = false;

        // Method 1: Try getNodeBox first (works for some elements)
        try {
          hasBox = instanceTree.getNodeBox(dbId, elementBox);
          if (!hasBox || elementBox.isEmpty()) {
            // Reset for next attempt
            elementBox.makeEmpty();
            hasBox = false;
          }
        } catch (e) {
          hasBox = false;
        }

        // Method 2: Get box from fragments directly using fragId2dbId mapping
        if (!hasBox) {
          // Find all fragments for this dbId
          for (
            let fragId = 0;
            fragId < fragments.fragId2dbId.length;
            fragId++
          ) {
            if (fragments.fragId2dbId[fragId] === dbId) {
              const fragBox = new THREE.Box3();
              fragList.getWorldBounds(fragId, fragBox);
              if (!fragBox.isEmpty()) {
                elementBox.union(fragBox);
                hasBox = true;
              }
            }
          }
        }

        // Method 3: Try enumNodeFragments as last resort
        if (!hasBox) {
          instanceTree.enumNodeFragments(
            dbId,
            (fragId: number) => {
              const fragBox = new THREE.Box3();
              fragList.getWorldBounds(fragId, fragBox);
              if (!fragBox.isEmpty()) {
                elementBox.union(fragBox);
                hasBox = true;
              }
            },
            true
          );
        }

        if (hasBox && !elementBox.isEmpty()) {
          checkedCount++;
          if (expandedBox.intersectsBox(elementBox)) {
            intersectingDbIds.push(dbId);
            // Log first few intersections for debugging
          }
        } else {
          boxFailures++;
        }
      });
      return intersectingDbIds;
    },
    [viewer, getTHREE]
  );

  // Check if element should be excluded
  const checkExclusion = (
    name: string,
    category: string,
    dbId: number
  ): { excluded: boolean; reason?: string } => {
    if (!name && !category) return { excluded: false };

    const lowerName = name?.toLowerCase() || "";
    const lowerCategory = category?.toLowerCase() || "";

    // Special check for very low dbIds (usually system elements)
    if (dbId < 10) {
      return { excluded: true, reason: "System element (low dbId)" };
    }

    // Check if category matches the name (indicates metadata/project info)
    if (DEFAULT_EXCLUSIONS.specialExclusions.categoryMatchesName) {
      if (category && name && category.includes(name)) {
        return {
          excluded: true,
          reason: "Metadata element (category contains name)",
        };
      }
    }

    // Check special patterns
    for (const pattern of DEFAULT_EXCLUSIONS.specialExclusions.patterns) {
      if (pattern.test(name)) {
        return { excluded: true, reason: `Pattern match: ${pattern.source}` };
      }
      if (pattern.test(category)) {
        return {
          excluded: true,
          reason: `Category pattern: ${pattern.source}`,
        };
      }
    }

    // Check exact name matches
    for (const exactName of DEFAULT_EXCLUSIONS.exactNames) {
      if (lowerName === exactName.toLowerCase()) {
        return { excluded: true, reason: `Exact match: ${exactName}` };
      }
    }

    // Check partial name matches
    for (const partialName of DEFAULT_EXCLUSIONS.partialNames) {
      if (lowerName.includes(partialName.toLowerCase())) {
        return { excluded: true, reason: `Contains: ${partialName}` };
      }
    }

    // Check category exclusions
    for (const excludedCategory of DEFAULT_EXCLUSIONS.categories) {
      if (lowerCategory.includes(excludedCategory.toLowerCase())) {
        return { excluded: true, reason: `Category: ${excludedCategory}` };
      }
    }

    return { excluded: false };
  };

  // Calculate distance between element centers
  const calculateDistance = useCallback(
    (box1: any, box2: any): number => {
      if (!viewer?.model) return 0;

      const THREE = getTHREE();
      if (!THREE) {
        return 0;
      }

      const center1 = box1.getCenter(new THREE.Vector3());
      const center2 = box2.getCenter(new THREE.Vector3());

      const distanceInModel = center1.distanceTo(center2);

      // Convert to feet
      const unitScale = viewer.model.getUnitScale();
      const distanceInMeters = distanceInModel * unitScale;
      const METERS_TO_FEET = 3.28084;

      return distanceInMeters * METERS_TO_FEET;
    },
    [viewer, getTHREE]
  );

  // Main search function
  const handleSearch = async () => {
    if (!viewer || !isModelLoaded) {
      toast.error("Viewer not ready. Please load a model first.");
      return;
    }

    if (!sourceElementId.trim()) {
      toast.warning("Please enter a source element ID");
      return;
    }

    setIsProcessing(true);
    const startTime = Date.now();


    try {
      // Resolve element ID to DbId
      let sourceDbId: number | null = null;
      const numId = parseInt(sourceElementId.trim());

      if (!isNaN(numId)) {
        sourceDbId = numId;
      } else {
        sourceDbId = await PropertiesService.findDbIdByRevitId(
          viewer,
          sourceElementId.trim()
        );
      }

      if (!sourceDbId) {
        toast.error(`Element ${sourceElementId} not found`);
        return;
      }


      // Get source element name
      const sourceProps = await PropertiesService.getElementProperties(
        viewer,
        sourceDbId
      );
      const sourceNameProp = sourceProps.find(
        (p) => p.displayName === "Name" || p.displayName === "Element Name"
      );
      const sourceName =
        sourceNameProp?.displayValue || `Element_${sourceDbId}`;

      // Get source bounding box with multiple fallback methods
      const instanceTree = viewer.model.getInstanceTree();

      const THREE = getTHREE();
      if (!THREE) {
        toast.error("THREE.js not available");
        return;
      }

      const sourceBox = new THREE.Box3();
      let gotBox = false;

      // Method 1: Try getNodeBox directly
      try {
        gotBox = instanceTree.getNodeBox(sourceDbId, sourceBox);
        if (gotBox) {
        }
      } catch (e) {
      }

      // Method 2: Try fragment enumeration
      if (!gotBox) {
        let hasFragments = false;
        instanceTree.enumNodeFragments(
          sourceDbId,
          (fragId: number) => {
            hasFragments = true;
            const fragList = viewer.model.getFragmentList();
            const fragBox = new THREE.Box3();
            fragList.getWorldBounds(fragId, fragBox);
            sourceBox.union(fragBox);
          },
          true
        );

        if (hasFragments && !sourceBox.isEmpty()) {
          gotBox = true;
        }
      }

      // Method 3: Try selection method
      if (!gotBox) {
        viewer.select([sourceDbId]);
        const selectionBox = viewer.utilities.getBoundingBox(true);
        viewer.clearSelection();

        if (selectionBox && !selectionBox.isEmpty()) {
          sourceBox.copy(selectionBox);
          gotBox = true;
        }
      }

      if (!gotBox || sourceBox.isEmpty()) {
        toast.error("Could not get bounding box for source element");
        return;
      }


      // Expand the bounding box
      const expandedBox = expandBoundingBox(sourceBox, expansion);

      // Log box sizes for debugging
      const sourceSize = new THREE.Vector3();
      sourceBox.getSize(sourceSize);
      const expandedSize = new THREE.Vector3();
      expandedBox.getSize(expandedSize);


      // Visualize expanded box if option is enabled
      if (showBoundingBox) {
        clearBoundingBoxOverlay(viewer);
        visualizeBoundingBox(viewer, expandedBox, "#00FF00", 0.2);
      }

      // Find intersecting elements
      const intersectingDbIds = findIntersectingElements(expandedBox);

      // Filter out the source element itself
      const filteredDbIds = intersectingDbIds.filter((id) => id !== sourceDbId);


      // Get properties for all intersecting elements
      const elementResults: ElementResult[] = [];

      for (const dbId of filteredDbIds) {
        try {
          const props = await PropertiesService.getElementProperties(
            viewer,
            dbId
          );
          const nameProp = props.find(
            (p) => p.displayName === "Name" || p.displayName === "Element Name"
          );
          const categoryProp = props.find((p) => p.displayName === "Category");

          const name = nameProp?.displayValue || `Element_${dbId}`;
          const category = categoryProp?.displayValue || "Unknown";

          // Check exclusion
          const { excluded, reason } = checkExclusion(
            name as string,
            category as string,
            dbId
          );

          // Calculate distance
          const elementBox = new THREE.Box3();
          let hasElementBox = false;

          try {
            hasElementBox = instanceTree.getNodeBox(dbId, elementBox);
          } catch (e) {
            // Try fragments if getNodeBox fails
            instanceTree.enumNodeFragments(
              dbId,
              (fragId: number) => {
                const fragList = viewer.model.getFragmentList();
                const fragBox = new THREE.Box3();
                fragList.getWorldBounds(fragId, fragBox);
                elementBox.union(fragBox);
                hasElementBox = true;
              },
              true
            );
          }

          const distance =
            hasElementBox && !elementBox.isEmpty()
              ? calculateDistance(sourceBox, elementBox)
              : 0;

          elementResults.push({
            dbId,
            name: name as string,
            category: category as string,
            distance,
            included: !excluded,
            excludeReason: reason,
          });
        } catch (error) {
        }
      }

      // Sort by distance
      elementResults.sort((a, b) => a.distance - b.distance);

      // Calculate statistics
      const includedCount = elementResults.filter((e) => e.included).length;
      const excludedCount = elementResults.filter((e) => !e.included).length;
      const searchTime = Date.now() - startTime;

      // Store result
      const result: IntersectionResult = {
        source: {
          dbId: sourceDbId,
          name: sourceName as string,
          originalBox: sourceBox,
          expandedBox: expandedBox,
        },
        elements: elementResults,
        statistics: {
          totalFound: elementResults.length,
          includedCount,
          excludedCount,
          searchTime,
        },
        expansion,
      };

      setResult(result);

      toast.success(
        `Found ${includedCount} relevant elements (${excludedCount} excluded)`
      );
    } catch (error) {
      toast.error("Error during search");
    } finally {
      setIsProcessing(false);
    }
  };

  // Visualize element
  const handleVisualize = (dbId: number, isSource: boolean = false) => {
    if (!viewer || !result) return;


    // Clear any previous highlights
    viewer.clearThemingColors();
    viewer.isolate([]); // Show all elements first
    viewer.showAll();

    const dbIds = isSource ? [dbId] : [result.source.dbId, dbId];

    const THREE = getTHREE();
    if (!THREE) {
      return;
    }

    // Apply colors
    if (isSource) {
      viewer.setThemingColor(dbId, new THREE.Vector4(0, 0, 1, 0.6)); // Blue
    } else {
      viewer.setThemingColor(
        result.source.dbId,
        new THREE.Vector4(0, 0, 1, 0.6)
      ); // Blue for source
      viewer.setThemingColor(dbId, new THREE.Vector4(0, 1, 0, 0.6)); // Green for target
    }

    // Isolate only the highlighted elements (like unchecked "Show all model elements")
    viewer.isolate(dbIds);

    // Fit view to show the elements
    viewer.fitToView(dbIds, viewer.model, true);

    // Force viewer update
    viewer.impl.invalidate(true, true, true);

  };

  // Visualize all included elements
  const handleVisualizeAll = () => {
    if (!viewer || !result) return;


    // Clear any previous highlights
    viewer.clearThemingColors();
    viewer.isolate([]); // Show all elements first
    viewer.showAll();

    // Collect all dbIds
    const includedDbIds = result.elements
      .filter((e) => e.included)
      .map((e) => e.dbId);

    const allDbIds = [result.source.dbId, ...includedDbIds];

    const THREE = getTHREE();
    if (!THREE) {
      return;
    }

    // Apply colors
    viewer.setThemingColor(result.source.dbId, new THREE.Vector4(0, 0, 1, 0.6)); // Blue for source
    includedDbIds.forEach((dbId) => {
      viewer.setThemingColor(dbId, new THREE.Vector4(0, 1, 0, 0.6)); // Green for included elements
    });

    // Isolate only the highlighted elements (like unchecked "Show all model elements")
    viewer.isolate(allDbIds);

    // Fit view to show all elements
    viewer.fitToView(allDbIds, viewer.model, true);

    // Force viewer update
    viewer.impl.invalidate(true, true, true);

  };

  // Export results
  const exportResults = () => {
    if (!result) return;

    const exportData = {
      timestamp: new Date().toISOString(),
      source: {
        id: sourceElementId,
        dbId: result.source.dbId,
        name: result.source.name,
      },
      expansion: result.expansion,
      statistics: result.statistics,
      includedElements: result.elements
        .filter((e) => e.included)
        .map((e) => ({
          dbId: e.dbId,
          name: e.name,
          category: e.category,
          distance: e.distance.toFixed(2) + " ft",
        })),
      excludedElements: result.elements
        .filter((e) => !e.included)
        .map((e) => ({
          dbId: e.dbId,
          name: e.name,
          category: e.category,
          distance: e.distance.toFixed(2) + " ft",
          excludeReason: e.excludeReason,
        })),
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `bbox-intersection-${result.source.dbId}-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);

    toast.success("Results exported to JSON");
  };

  // Clear all
  const handleClear = () => {
    setSourceElementId("");
    setResult(null);

    if (viewer) {
      // Clear all highlights and show all elements
      viewer.clearThemingColors();
      viewer.isolate([]);
      viewer.showAll();
      viewer.impl.invalidate(true, true, true);
      clearBoundingBoxOverlay(viewer);
    }

  };

  return (
    <div className="mt-4 bg-dark-100 rounded-lg border border-gray-700">
      {/* Header */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-dark-200 transition-colors rounded-lg"
      >
        <div className="flex items-center gap-2">
          <Box className="w-5 h-5 text-cyan-400" />
          <span className="font-semibold text-gray-100">
            Bounding Box Intersection Tool
          </span>
        </div>
        <div className="flex items-center gap-2">
          {!isCollapsed && result && (
            <span className="text-xs text-white">
              {result.statistics.includedCount} found
            </span>
          )}
          {isCollapsed ? (
            <ChevronRight className="w-5 h-5 text-white" />
          ) : (
            <ChevronDown className="w-5 h-5 text-white" />
          )}
        </div>
      </button>

      {/* Content */}
      {!isCollapsed && (
        <div className="px-4 pb-4 border-t border-gray-700">
          {/* Input Section */}
          <div className="mt-4 space-y-3">
            {/* Source Element */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Source Element (DbId or Revit ID)
              </label>
              <input
                type="text"
                value={sourceElementId}
                onChange={(e) => setSourceElementId(e.target.value)}
                placeholder="Enter element ID"
                className="w-full px-3 py-2 bg-dark-200 text-gray-100 rounded border border-gray-600 focus:border-cyan-500 focus:outline-none"
              />
            </div>

            {/* Expansion Values */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Expansion Values (feet)
              </label>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-xs text-white">X-axis</label>
                  <input
                    type="number"
                    value={expansion.x}
                    onChange={(e) =>
                      setExpansion({
                        ...expansion,
                        x: parseFloat(e.target.value) || 0,
                      })
                    }
                    step="0.5"
                    min="0"
                    className="w-full px-2 py-1 bg-dark-200 text-gray-100 rounded border border-gray-600 focus:border-cyan-500 focus:outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs text-white">Y-axis</label>
                  <input
                    type="number"
                    value={expansion.y}
                    onChange={(e) =>
                      setExpansion({
                        ...expansion,
                        y: parseFloat(e.target.value) || 0,
                      })
                    }
                    step="0.5"
                    min="0"
                    className="w-full px-2 py-1 bg-dark-200 text-gray-100 rounded border border-gray-600 focus:border-cyan-500 focus:outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs text-white">Z-axis</label>
                  <input
                    type="number"
                    value={expansion.z}
                    onChange={(e) =>
                      setExpansion({
                        ...expansion,
                        z: parseFloat(e.target.value) || 0,
                      })
                    }
                    step="0.5"
                    min="0"
                    className="w-full px-2 py-1 bg-dark-200 text-gray-100 rounded border border-gray-600 focus:border-cyan-500 focus:outline-none text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Options */}
            <div className="flex items-center gap-4 text-sm">
              <label className="flex items-center gap-2 text-gray-300">
                <input
                  type="checkbox"
                  checked={showExcluded}
                  onChange={(e) => setShowExcluded(e.target.checked)}
                  className="w-4 h-4"
                />
                Show excluded elements
              </label>
              <label className="flex items-center gap-2 text-gray-300">
                <input
                  type="checkbox"
                  checked={showBoundingBox}
                  onChange={(e) => setShowBoundingBox(e.target.checked)}
                  className="w-4 h-4"
                />
                Show expanded box
              </label>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              <button
                onClick={handleSearch}
                disabled={isProcessing || !isModelLoaded}
                className={`flex-1 px-4 py-2 rounded font-medium transition-colors flex items-center justify-center gap-2 ${
                  isProcessing || !isModelLoaded
                    ? "bg-gray-600 text-white cursor-not-allowed"
                    : "bg-cyan-600 hover:bg-cyan-700 text-white"
                }`}
              >
                {isProcessing ? (
                  <>
                    <span className="animate-spin">⟳</span>
                    Searching...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4" />
                    Find Nearby Elements
                  </>
                )}
              </button>

              <button
                onClick={handleClear}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded font-medium transition-colors"
              >
                Clear
              </button>
            </div>
          </div>

          {/* Results Section */}
          {result && (
            <div className="mt-4 space-y-3">
              {/* Statistics */}
              <div className="p-3 bg-dark-200 rounded text-sm">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium text-gray-200">Results</span>
                  <div className="flex gap-2">
                    <button
                      onClick={handleVisualizeAll}
                      className="px-3 py-1 bg-cyan-600 hover:bg-cyan-700 text-white rounded text-xs font-medium transition-colors flex items-center gap-1"
                    >
                      <Eye className="w-3 h-3" />
                      Visualize All
                    </button>
                    <button
                      onClick={exportResults}
                      className="px-3 py-1 bg-gray-600 hover:bg-gray-700 text-white rounded text-xs font-medium transition-colors flex items-center gap-1"
                    >
                      <Download className="w-3 h-3" />
                      Export
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs text-white">
                  <div>Total Found: {result.statistics.totalFound}</div>
                  <div>Search Time: {result.statistics.searchTime}ms</div>
                  <div className="text-green-400">
                    Included: {result.statistics.includedCount}
                  </div>
                  <div className="text-orange-400">
                    Excluded: {result.statistics.excludedCount}
                  </div>
                </div>
              </div>

              {/* Element List */}
              <div className="max-h-64 overflow-y-auto custom-scrollbar space-y-1">
                {/* Included Elements */}
                {result.elements
                  .filter((e) => e.included)
                  .map((element) => (
                    <div
                      key={element.dbId}
                      className="flex items-center justify-between p-2 bg-dark-200 hover:bg-dark-300 rounded transition-colors"
                    >
                      <div className="flex-1">
                        <div className="text-sm text-gray-200">
                          <span className="text-green-400">✓</span>{" "}
                          {element.name}
                        </div>
                        <div className="text-xs text-white">
                          {element.category} • {element.distance.toFixed(2)} ft
                          • DbId: {element.dbId}
                        </div>
                      </div>
                      <button
                        onClick={() => handleVisualize(element.dbId)}
                        className="p-1 text-white hover:text-cyan-400 transition-colors"
                        title="Visualize element"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </div>
                  ))}

                {/* Excluded Elements (if enabled) */}
                {showExcluded &&
                  result.elements
                    .filter((e) => !e.included)
                    .map((element) => (
                      <div
                        key={element.dbId}
                        className="flex items-center justify-between p-2 bg-dark-200 hover:bg-dark-300 rounded transition-colors opacity-60"
                      >
                        <div className="flex-1">
                          <div className="text-sm text-white">
                            <span className="text-orange-400">✗</span>{" "}
                            {element.name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {element.excludeReason} •{" "}
                            {element.distance.toFixed(2)} ft
                          </div>
                        </div>
                        <button
                          onClick={() => handleVisualize(element.dbId)}
                          className="p-1 text-gray-500 hover:text-gray-300 transition-colors"
                          title="Visualize element"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
              </div>

              {/* Warning if many excluded */}
              {result.statistics.excludedCount > 0 && !showExcluded && (
                <div className="flex items-center gap-2 p-2 bg-orange-900/20 border border-orange-800/50 rounded text-xs text-orange-400">
                  <AlertCircle className="w-4 h-4" />
                  <span>
                    {result.statistics.excludedCount} elements excluded. Enable
                    "Show excluded" to see them.
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Debug Info */}
          <div className="mt-3 p-2 bg-dark-200 rounded text-xs text-white">
            <div>Viewer Status: {viewer ? "✓ Initialized" : "✗ Not Ready"}</div>
            <div>
              Model Status: {isModelLoaded ? "✓ Loaded" : "✗ Not Loaded"}
            </div>
            {viewer && <div>Model ID: {viewer.model?.id || "None"}</div>}
          </div>
        </div>
      )}
    </div>
  );
}
